export type ConversationKind =
  | "project_team"
  | "contributor_group"
  | "direct"
  | "customer"

/** A conversation always hangs off one of these. */
export type RecordKind = "lead" | "project"

export type Visibility =
  | "internal"
  | "includes_subcontractors"
  | "customer"
  | "private"

export interface MessagingRecord {
  id: string
  name: string
  record_kind: RecordKind
  address: string
}

export type NotificationLevel = "all" | "mentions" | "muted"

export interface Participant {
  id: string
  full_name: string
  email: string
  role: number
  role_label: string
  avatar: string
}

export interface MessageAttachment {
  id: string
  file_name: string
  file_size: number | null
  content_type: string
  is_image: boolean
  download_url: string
  saved_to_project_files: boolean
  saved_document: string | null
}

export interface ReplyPreview {
  id: string
  sender_name: string
  body: string
  is_deleted: boolean
}

export interface Message {
  id: string
  conversation: string
  sender: Participant | null
  body: string
  reply_to: ReplyPreview | null
  attachments: MessageAttachment[]
  mentioned_user_ids: string[]
  /** Display names for this message's mentions, so one of somebody since
   *  removed still reads as their name rather than "unknown". */
  mentioned_names: Record<string, string>
  edited_at: string | null
  is_deleted: boolean
  client_token: string | null
  created: string
}

/**
 * A message the composer has handed over but the server has not yet
 * acknowledged. It carries the same `client_token` the request does, which is
 * what lets the acknowledgement replace it instead of arriving beside it.
 */
export interface PendingMessage {
  client_token: string
  conversation: string
  body: string
  reply_to: ReplyPreview | null
  attachment_names: string[]
  created: string
  status: "sending" | "failed"
}

export interface LastMessagePreview {
  id: string
  sender_name: string
  body: string
  is_deleted: boolean
  created: string
}

export interface Conversation {
  id: string
  kind: ConversationKind
  /** The room's own name: "Team Chat", or the other person. Never the record's. */
  title: string
  /** "Customer Chat" / "Direct" under the title; empty for a team chat. */
  subtitle: string
  record_id: string
  record_kind: RecordKind
  record_name: string
  visibility: Visibility
  /** "Internal" / "Customer-visible" / "Private" — the category alone. */
  visibility_label: string
  composer_placeholder: string
  project: string | null
  project_name: string
  participants: Participant[]
  unread_count: number
  last_message: LastMessagePreview | null
  last_message_at: string | null
  notification_level: NotificationLevel
  can_send: boolean
  /** Why the composer is off: "project_archived", "project_team_needs_members". */
  send_blocked_reason: string
  can_manage_members: boolean
  /** Whether this reader may change the project's assignments. */
  can_manage_project_team: boolean
  /** The roster's size, for the row and the header. */
  participant_count: number
  /** One line naming who can read this room. */
  audience_note: string
  draft: string
  created: string
}

export interface SearchResult {
  id: string
  conversation: string
  conversation_title: string
  conversation_kind: ConversationKind
  record_id: string
  record_kind: RecordKind
  record_name: string
  project_id: string
  project_name: string
  sender_name: string
  excerpt: string
  attachment_names: string[]
  created: string
}

export interface ProjectConversations {
  project_id: string
  project_name: string
  team_chat: Conversation | null
  customer_chat: Conversation | null
  /** Explicitly assembled rooms; a project may have many. */
  groups: Conversation[]
  /** The caller's own private threads on this project. */
  direct_chats: Conversation[]
  customer_setup: {
    ready: boolean
    missing_project_manager: boolean
    missing_owner: boolean
    missing_customer_account: boolean
    owner_role: "project_manager" | "sales_manager"
  }
  archived: boolean
  missing_branch: boolean
  can_manage_members: boolean
  /** Drives the Chat tab's badge. */
  unread_total: number
}

export interface ConversationMembers {
  participants: Participant[]
  /** Who the picker may offer — people on the project, nobody else. */
  eligible: Participant[]
  can_manage_members: boolean
}

export type ChatFilter = "all" | ConversationKind

/** The primary filter: which kind of record, or everything. */
export type RecordFilter = "all" | RecordKind

export type SearchScope = "conversations" | "messages"

export const WS_EVENT = {
  INITIAL_STATE: "initial_state",
  SUBSCRIBED: "subscribed",
  READ_STATE: "read_state",
  CONVERSATION_EVENT: "conversation.event"
} as const

export const CONVERSATION_EVENT = {
  MESSAGE_CREATED: "message.created",
  MESSAGE_UPDATED: "message.updated",
  MESSAGE_DELETED: "message.deleted",
  MEMBERSHIP: "conversation.membership",
  ACCESS_REVOKED: "conversation.access_revoked",
  READ: "conversation.read"
} as const
